import { useContext, useEffect, useState, useMemo } from 'react';
import { UserContext } from 'context/User/UserContext';
import { getJobCategory, getJobCategoryMapping } from '../_api';
import { JobCategoryMappingDTO, SearchResult } from '../_classes';
import PageWithBreadcrumbs from '../layouts/PageWithBreadcrumbs';
import JobCategoryMappingForm from './FormView';
import FormView from './NewFormView';

/**
 * Epic Job Category Mapping Form for creating new or editing existing job category mapping
 * @param {any} props properties from router
 * @returns Job Category Mapping Editor page view
 */
const EditorView = props => {
	const query = new URLSearchParams(props.location.search);
	const idString = query.get('id');

	/**
	 * Grab the ID in the new format, which is a CSV passed in
	 */
	const id = useMemo(() => {
		if (idString.includes(',')) {
			return idString.split(',');
		} else {
			return idString ? [idString] : [];
		}
	}, [idString]);

	const isNew = id.length === 0;

	/**
	 * @type {import('context/User/UserContext').ContextUser}
	 */
	const contextUser = useContext(UserContext);
	const maintenanceUser = contextUser?.userInfo?.impersonationProfile
		? { ...contextUser?.userInfo?.impersonationProfile }
		: { ...contextUser.userInfo.userProfile };

	const multipleJobCategoriesExist = id.length > 1 ? true : false;

	const [sJobCategoryMapping, setJobCategoryMapping] = useState([]);

	useEffect(() => {
		let active = true;

		// Get all mappings if there are multiples
		async function fetchApi() {
			const mappings = await Promise.all(
				id.map(async singleId => {
					// Fetch the base job category mapping info
					const selectedJobCategoryMapping =
						await getJobCategoryMapping(singleId);

					// Fetch/set job category
					const jobCategoryDetails = await getJobCategory(
						selectedJobCategoryMapping.jobCategoryID
					);
					selectedJobCategoryMapping.setJobCategory(
						new SearchResult({
							Key: jobCategoryDetails.jobCategoryID,
							Value: jobCategoryDetails.name,
						})
					);

					// Set job title
					selectedJobCategoryMapping.setJobTitle(
						new SearchResult({
							Key: -1,
							Value: selectedJobCategoryMapping.jobTitle,
						})
					);

					// Set department
					selectedJobCategoryMapping.setDepartment(
						new SearchResult({
							Key: -1,
							Value: selectedJobCategoryMapping.department,
						})
					);

					return selectedJobCategoryMapping;
				})
			);

			if (active) {
				setJobCategoryMapping(mappings);
			}
		}

		if (active && id && id.length > 0) {
			fetchApi();
		}
		if (active && (!id || id.length === 0)) {
			setJobCategoryMapping([new JobCategoryMappingDTO()]);
		}

		return () => {
			active = false;
		};
	}, [id]);

	return (
		<PageWithBreadcrumbs
			currentPageLabel={
				isNew
					? 'New Job Category Mapping'
					: 'Job Category Mapping Editor'
			}
		>
			{(isNew || sJobCategoryMapping) && !multipleJobCategoriesExist && (
				<JobCategoryMappingForm
					jobCategoryMapping={sJobCategoryMapping[0]}
					isNew={isNew}
					maintenanceInformation={maintenanceUser}
				/>
			)}
			{multipleJobCategoriesExist && sJobCategoryMapping.length > 0 && (
				<FormView
					maintenanceInformation={maintenanceUser}
					jobCategoryMapping={sJobCategoryMapping}
				/>
			)}
		</PageWithBreadcrumbs>
	);
};

export default EditorView;
